import React, { useEffect, useState } from 'react'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone'
import { useNavigate } from 'react-router-dom'

import useUser from '../../services/useUser'
import {
  fetchLegacyRejectedSickDayNotifications,
  fetchNotificationUserContext,
  fetchNotificationsFallbackFromServer,
  fetchScopedSickDayNotifications,
  getLegacyReadIds,
  subscribeToUnreadNotifications,
} from '../../services/firebase/notifications'

function countUnread(notifications) {
  if (!Array.isArray(notifications) || notifications.length === 0) {
    return 0
  }

  const legacyReadIds = getLegacyReadIds()
  const withReadState = notifications.map((n) =>
    legacyReadIds.includes(n?.id) ? { ...n, isRead: true } : n
  )
  return withReadState.filter((n) => !n?.isRead).length
}

export default function NotificationsBell() {
  const user = useUser()
  const claims = user?.customClaims
  const claimsLoading = user?.claimsLoading
  const navigate = useNavigate()
  const [directUnreadCount, setDirectUnreadCount] = useState(0)
  // null = snapshot not yet received; ≥0 = number of total Firestore notifications
  const [directTotalCount, setDirectTotalCount] = useState(null)
  const [fallbackUnreadCount, setFallbackUnreadCount] = useState(0)
  const [legacyReadVersion, setLegacyReadVersion] = useState(0)

  // Use Firestore unread count whenever ANY Firestore notifications exist (even if all are read).
  // Only fall back to legacy when Firestore has no notifications at all.
  const unreadCount = (directTotalCount !== null && directTotalCount > 0)
    ? directUnreadCount
    : fallbackUnreadCount

  // Re-count fallback when user marks a legacy notification as read
  useEffect(() => {
    function handleStorageChange(event) {
      if (event.key === 'legacy_read_notification_ids') {
        setLegacyReadVersion((v) => v + 1)
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  useEffect(() => {
    if (!user?.uid) {
      setDirectUnreadCount(0)
      setDirectTotalCount(null)
      setFallbackUnreadCount(0)
      return undefined
    }

    return subscribeToUnreadNotifications(user.uid, (notifications) => {
      setDirectTotalCount(notifications.length)
      setDirectUnreadCount(countUnread(notifications))
    }, () => {
      setDirectTotalCount(0)
      setDirectUnreadCount(0)
    })
  }, [user?.uid])

  useEffect(() => {
    // Run fallback only when we KNOW there are no Firestore notifications at all.
    // directTotalCount === null means the snapshot hasn't arrived yet — wait.
    // directTotalCount > 0 means Firestore has notifications — use Firestore count.
    if (!user?.uid || claimsLoading || directTotalCount === null || directTotalCount > 0) {
      setFallbackUnreadCount(0)
      return undefined
    }

    let cancelled = false

    const loadFallbackUnreadCount = async () => {
      try {
        let employerId = claims?.role === 'employee' ? claims?.employer : null
        let calculationId = claims?.role === 'employee' ? claims?.employee : null

        if (!employerId || !calculationId) {
          const userContext = await fetchNotificationUserContext(user.uid)
          if (cancelled) return

          employerId = employerId || userContext.employerId
          calculationId = calculationId || userContext.calculationId
        }

        if (employerId && calculationId) {
          const legacyNotifications = await fetchLegacyRejectedSickDayNotifications(
            employerId,
            calculationId
          )

          if (cancelled) return
          if (legacyNotifications.length > 0) {
            setFallbackUnreadCount(countUnread(legacyNotifications))
            return
          }

          const scopedNotifications = await fetchScopedSickDayNotifications(
            employerId,
            calculationId
          )

          if (cancelled) return
          if (scopedNotifications.length > 0) {
            setFallbackUnreadCount(countUnread(scopedNotifications))
            return
          }
        }

        const serverNotifications = await fetchNotificationsFallbackFromServer()
        if (!cancelled) {
          setFallbackUnreadCount(countUnread(serverNotifications))
        }
      } catch (error) {
        if (!cancelled) {
          setFallbackUnreadCount(0)
        }
      }
    }

    loadFallbackUnreadCount()

    return () => {
      cancelled = true
    }
  }, [claims?.employee, claims?.employer, claims?.role, claimsLoading, directTotalCount, legacyReadVersion, user?.uid])

  if (!user?.uid) {
    return null
  }

  return (
    <Tooltip title="הודעות">
      <IconButton
        color="inherit"
        onClick={() => navigate('/notifications')}
        aria-label={unreadCount > 0 ? `הודעות, ${unreadCount} לא נקראו` : 'הודעות'}
      >
        <Badge badgeContent={unreadCount} max={9} color="error">
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>
    </Tooltip>
  )
}